import { FC, useState } from 'react';
import { SubPageHeader } from '@components/SubPageHeader/SubPageHeader';
import { Table } from '@dynamika-shared/ui/Table/Table';
import { NoContent } from '@dynamika-shared/ui/NoContent/NoContent';
import { makeSiteColumns } from './SitesTable.columns';
import {
  OnSortColumnType,
  Pagination,
} from '@dynamika-shared/ui/Table/Table.types';
import { SiteConfigurationType } from '@dynamika-shared/types/site';
import { SiteFormDrawer } from './SiteFormDrawer/SiteFormDrawer';
import { Modal } from '@dynamika-shared/ui/Modal/Modal';
import { useParams } from 'react-router-dom';
import { useClinicalTrialSlice } from '@restore/clinicalTrial/clinicalTrial.slice';

type ClinicalTrialSitesPageViewProps = {
  loading?: boolean;
  sitesData: SiteConfigurationType[];
  totalItems: number;
  pagination: Pagination;
  onSort: OnSortColumnType;
  sortState?: string;
};
export type SiteMode = 'SITE_CREATE' | 'SITE_EDIT';

export const ClinicalTrialSitesPageView: FC<
  ClinicalTrialSitesPageViewProps
> = ({ loading, sitesData, totalItems, onSort, sortState, pagination }) => {
  const params = useParams();
  const [deleteSite, setSiteForDelete, deleteInProgress, siteForDelete] =
    useClinicalTrialSlice((state, actions) => [
      actions.deleteSite,
      actions.setSiteForDelete,
      state.clinicalTrialActionLoading,
      state.siteForDelete,
    ]);
  const [siteMode, setSiteMode] = useState<SiteMode>();
  const [currentSite, setCurrentSite] = useState<SiteConfigurationType>();
  const siteColumns = makeSiteColumns({
    onSiteClick: (site, procedure) => {
      if (procedure === 'edit') {
        setSiteMode('SITE_EDIT');
        setCurrentSite(site);
      } else {
        setSiteForDelete(site.id);
      }
    },
  });

  const deleteSiteRequest = () => {
    if (params.id && siteForDelete) {
      try {
        deleteSite({ clinicalTrialId: params.id, siteId: siteForDelete });
      } finally {
        setSiteForDelete(undefined);
      }
    }
  };

  return (
    <div
      className='relative flex min-h-full grow flex-col'
      data-testid='ClinicalTrialSitesPageView'
    >
      <SubPageHeader
        title='Sites'
        buttonText='Site'
        showButton={Boolean(totalItems)}
        action={() => setSiteMode('SITE_CREATE')}
      />
      <div className='mt-3 flex grow flex-col'>
        <Table
          columns={siteColumns}
          data={sitesData}
          totalItems={totalItems}
          loading={loading}
          onSort={onSort}
          sortState={sortState}
          pagination={pagination}
          pageSizeOptions={[20, 50, 100]}
          noContentMessage={
            <NoContent
              action={() => setSiteMode('SITE_CREATE')}
              actionText={'Add Site'}
              iconName={'sites'}
              title={
                <span
                  data-testid='NoSites'
                  className='text-1424 text-spanishGray'
                >
                  No{' '}
                  <span className='font-semibold text-chineseBlack'>Sites</span>{' '}
                  here yet! <br /> Click the button to add a site!
                </span>
              }
            />
          }
        />
      </div>
      {!!siteMode && (
        <SiteFormDrawer
          mode={siteMode}
          close={() => {
            setSiteMode(undefined);
            setCurrentSite(undefined);
          }}
          currentSite={currentSite}
        />
      )}

      <Modal
        open={!!siteForDelete}
        width={440}
        onCancel={() => {
          setSiteForDelete(undefined);
        }}
        onOk={deleteSiteRequest}
        okText='Delete'
        centered
        cancelText='Cancel'
        maskClosable={false}
        mask={false}
        okClassName='ant-btn-dangerous'
        classNames={{
          header: 'text-left',
          content: 'text-center !rounded-[2.4rem] !p-[4rem]',
          footer: 'flex justify-center',
        }}
        title='Delete Site'
        titleClassName={'text-left'}
        okProps={{
          disabled: deleteInProgress,
        }}
        cancelProps={{
          disabled: deleteInProgress,
        }}
      >
        Are you sure you want to delete this Site?
        <br />
        This action cannot be undone
      </Modal>
    </div>
  );
};
